/**
 * useNavItemStyles Hook
 *
 * Provides consistent styling for navigation menu items based on:
 * - Drawer open/closed state
 * - Nesting level (0 = top level, 1 = nested, 2 = deeply nested)
 * - Selection state
 *
 * Uses theme tokens for colors to support all 4 theme variants.
 */

import { useTheme } from '@mui/material/styles';

/**
 * Navigation item styles hook
 * @param {Object} options
 * @param {boolean} options.isOpen - Is the drawer open
 * @param {number} options.nestingLevel - 0 = top level, 1 = nested, 2 = deeply nested
 * @returns {Object} Style objects for list items and icons
 */
export const useNavItemStyles = ({ isOpen = true, nestingLevel = 0 } = {}) => {
  const theme = useTheme();

  return {
    /**
     * Styles for ListItemButton
     */
    listItem: {
      borderRadius: isOpen ? '20px' : '10px',
      margin: isOpen ? theme.spacing(0.375, 1.5) : theme.spacing(0.375, 0.5),
      padding: isOpen ? theme.spacing(1.25, 2) : theme.spacing(1.25),
      paddingLeft: isOpen ? (nestingLevel === 0 ? theme.spacing(2) : theme.spacing(2 + nestingLevel * 0.5)) : theme.spacing(1.25),
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      justifyContent: isOpen ? 'flex-start' : 'center',
      minHeight: isOpen ? 'auto' : 40,
      transition: theme.transitions.create(
        ['background-color', 'padding', 'transform', 'margin', 'border-radius'],
        {
          duration: theme.transitions.duration.short,
        }
      ),
      // Hide text when drawer is closed
      '& .MuiListItemText-root': {
        opacity: isOpen ? 1 : 0,
        display: isOpen ? 'block' : 'none',
        transition: theme.transitions.create('opacity', {
          duration: theme.transitions.duration.short,
        }),
      },
      // Compact icon spacing when collapsed
      '& .MuiListItemIcon-root': {
        minWidth: isOpen ? 40 : 24,
      },
      '&:focus': {
        outline: 'none',
      },
      '&:hover': {
        backgroundColor: theme.palette.background?.navHover || 'rgba(0, 0, 0, 0.04)',
        transform: isOpen ? 'translateX(2px)' : 'scale(1.05)',
      },
      '&:active': {
        transform: 'scale(0.98)',
      },
      '&.Mui-selected': {
        backgroundColor: theme.palette.background?.navActive || 'rgba(0, 0, 0, 0.08)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '20%',
          bottom: '20%',
          width: isOpen ? 4 : 3,
          borderRadius: '0 4px 4px 0',
          backgroundColor: theme.palette.primary.main,
          animation: 'slideIn 200ms ease-out',
        },
        '&:hover': {
          backgroundColor: theme.palette.background?.navActive || 'rgba(0, 0, 0, 0.08)',
        },
      },
      '@keyframes slideIn': {
        from: { opacity: 0, transform: 'scaleY(0)' },
        to: { opacity: 1, transform: 'scaleY(1)' },
      },
    },

    /**
     * Get icon styles based on selection state
     * @param {boolean} isSelected - Is the menu item selected
     * @returns {Object} Icon style object
     */
    icon: (isSelected) => ({
      color: isSelected
        ? theme.palette.secondary.main
        : theme.palette.primary.main,
      transition: theme.transitions.create('color', {
        duration: theme.transitions.duration.short,
      }),
      minWidth: isOpen ? 40 : 'auto',
      justifyContent: 'center',
      // Smaller icons when drawer collapsed
      fontSize: isOpen ? '1.65rem' : '1.4rem',
    }),

    /**
     * Styles for nested list container
     */
    nestedList: {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.02)'
          : 'rgba(0, 0, 0, 0.02)',
      borderRadius: theme.spacing(1),
      margin: theme.spacing(0, 1),
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
    },

    /**
     * Styles for the expand/collapse icon
     */
    expandIcon: {
      color: theme.palette.primary.main,
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.short,
      }),
    },
  };
};

export default useNavItemStyles;
